import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import type { UserRole } from '../types';

export interface IdentityCheckResult {
  allowed: boolean;
  role: string | null;
  personId: string | null;
  personName: string | null;
  personEmail: string | null;
}

/** Role mapping from identity service access roles to training-tracker roles */
const ROLE_MAP: Record<string, UserRole> = {
  admin: 'admin',
  instructor: 'instructor',
  individual: 'individual',
  outside_observer: 'outside_observer',
};

/**
 * Call the identity service to check if the current user has access
 * to the training-tracker and what role they should have.
 *
 * Returns null if the identity service is unavailable (demo mode, network error).
 */
export async function checkIdentity(): Promise<IdentityCheckResult | null> {
  if (!functions) return null;

  try {
    const fn = httpsCallable<void, IdentityCheckResult>(functions, 'identityCheck');
    const result = await fn();
    return result.data;
  } catch (err) {
    console.warn('Identity service check failed (falling back to local auth):', err);
    return null;
  }
}

/**
 * Map an identity service role string to a training-tracker UserRole.
 * Falls back to the provided default if the role is unknown or null.
 */
export function mapIdentityRole(identityRole: string | null, defaultRole: UserRole): UserRole {
  if (!identityRole) return defaultRole;
  return ROLE_MAP[identityRole] ?? defaultRole;
}
